import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  List,
  Typography,
  Tag,
  Button,
  Empty,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  ClockCircleOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { removeFromHistory, clearHistory } from '../../redux/slices/aiReportHistorySlice';
import { setQuery, generateReport } from '../../redux/slices/aiReportsSlice';
import { formatDate } from '../../utils/reportFormatters';
import styles from './ReportHistory.module.css';

const { Text, Title } = Typography;

const ReportHistory = () => {
  const dispatch = useDispatch();
  const { reports } = useSelector((state) => state.aiReportHistory);

  // Handle re-run report
  const handleRerun = (query) => {
    dispatch(setQuery(query));
    dispatch(generateReport({ query, outputFormat: 'html', priority: 'normal' }));
  };

  // Handle delete from history
  const handleDelete = (reportId) => {
    dispatch(removeFromHistory(reportId));
  };

  // Handle clear all
  const handleClearAll = () => {
    dispatch(clearHistory());
  };

  if (reports.length === 0) {
    return (
      <Card className={styles.historyCard} title="Recent Reports">
        <Empty
          description="No recent reports"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card
      className={styles.historyCard}
      title={
        <div className={styles.header}>
          <Title level={5}>Recent Reports</Title>
          <Popconfirm
            title="Clear all history?"
            onConfirm={handleClearAll}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" size="small">
              Clear All
            </Button>
          </Popconfirm>
        </div>
      }
    >
      <List
        dataSource={reports}
        renderItem={(report) => (
          <List.Item className={styles.listItem}>
            <div className={styles.listItemContent}>
              <div className={styles.listItemTop}>
                <Text className={styles.queryText} ellipsis={{ tooltip: report.query }}>
                  {report.query}
                </Text>
                <div className={styles.actions}>
                  <Tooltip title="Re-run this query">
                    <Button
                      type="text"
                      icon={<ReloadOutlined />}
                      onClick={() => handleRerun(report.query)}
                      size="small"
                    />
                  </Tooltip>
                  <Popconfirm
                    title="Delete this report?"
                    onConfirm={() => handleDelete(report.reportId)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                    />
                  </Popconfirm>
                </div>
              </div>
              <div className={styles.metadata}>
                <Text type="secondary" className={styles.time}>
                  <ClockCircleOutlined /> {formatDate(report.generatedAt)}
                </Text>
                <Tag color="blue">{report.recordCount} records</Tag>
              </div>
            </div>
          </List.Item>
        )}
      />
    </Card>
  );
};

export default ReportHistory;
